/**
 * Collects YMQ / product option data for Shopify contact form quote submissions.
 * Used by product page inline forms, collection iframe popup, and fallback handlers.
 */
(function (global) {
  'use strict';

  function sanitizeContactLabel(label) {
    return String(label || '')
      .replace(/\*/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function contactFieldName(label) {
    const clean = sanitizeContactLabel(label);
    return clean ? 'contact[' + clean + ']' : '';
  }

  function isInternalPropertyKey(key) {
    const k = String(key || '');
    return k.startsWith('_') || k === '__ymq_option_info';
  }

  function getInputDisplayValue(input, box) {
    if (!input) return '';

    if (input.tagName === 'SELECT') {
      if (!input.value) return '';
      const opt = input.options[input.selectedIndex];
      if (opt && opt.hasAttribute('data-placeholder')) return '';
      let value = opt ? opt.text : input.value;
      if (box) {
        const pseudo = box.querySelector('.ymq-app-select-single .ymq-option-value');
        if (pseudo) value = pseudo.textContent.trim();
      }
      return value;
    }

    const type = (input.type || '').toLowerCase();
    if (type === 'text' || type === 'number') {
      return (input.value || '').trim();
    }

    if (type === 'radio' || type === 'checkbox') {
      if (!input.checked) return '';
      const label = input.closest('label');
      if (label) {
        const valueSpan = label.querySelector('.ymq-option-value');
        if (valueSpan) return valueSpan.textContent.trim();
        const fallbackSpan = label.querySelector(
          '.ymq-option-swatches-label, .ymq-option-button-text, .ymq-option-radio-text'
        );
        if (fallbackSpan) {
          return fallbackSpan.textContent.replace(/\s+/g, ' ').replace(/\(\s*\+\s*.*?\)/g, '').trim();
        }
      }
      return (input.value || 'on').trim();
    }

    return (input.value || '').trim();
  }

  function collectFromSummaryList() {
    const map = {};
    document.querySelectorAll('#custom-summary-list .summary-list-item').forEach(function (item) {
      const nameEl = item.querySelector('.summary-item-name');
      const valueEl = item.querySelector('.summary-item-value');
      if (!nameEl || !valueEl) return;
      const name = sanitizeContactLabel(nameEl.textContent);
      const value = (valueEl.textContent || '').trim();
      if (name && value) map[name] = value;
    });
    return map;
  }

  function collectFromYmqBoxes() {
    const map = {};
    document.querySelectorAll('.ymq-options-box').forEach(function (box) {
      if (box.classList.contains('ymq-option-condition-hide') || box.style.display === 'none') return;

      const titleEl = box.querySelector('.ymq_lable, .ymq-label, .ymq-option-title-wrapper > span:first-child');
      let optionName = box.getAttribute('ymq-option-label');
      if (!optionName) {
        optionName = titleEl ? sanitizeContactLabel(titleEl.textContent) : '';
      }
      optionName = sanitizeContactLabel(optionName);
      if (!optionName) return;

      const inputs = box.querySelectorAll(
        'input:checked:not(:disabled):not(.ymq-option-condtion-disabled), select:not(:disabled):not(.ymq-option-condtion-disabled), input[type="text"]:not(:disabled):not(.ymq-option-condtion-disabled), input[type="number"]:not(:disabled):not(.ymq-option-condtion-disabled)'
      );

      const values = [];
      inputs.forEach(function (input) {
        const value = getInputDisplayValue(input, box);
        if (value) values.push(value);
      });

      if (values.length) {
        map[optionName] = values.join(', ');
      }
    });
    return map;
  }

  function collectFromProperties() {
    const map = {};
    document
      .querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]')
      .forEach(function (el) {
        if (el.disabled) return;
        const match = String(el.name || '').match(/^properties\[(.+)\]$/);
        if (!match) return;
        const key = match[1];
        if (!key || isInternalPropertyKey(key)) return;

        const tag = (el.tagName || '').toUpperCase();
        const type = (el.type || '').toLowerCase();
        if (type === 'button' || type === 'submit' || type === 'file') return;

        let value = '';
        if (tag === 'SELECT') {
          value = getInputDisplayValue(el, el.closest('.ymq-options-box'));
        } else if (type === 'checkbox' || type === 'radio') {
          if (el.checked) value = getInputDisplayValue(el, el.closest('.ymq-options-box'));
        } else {
          value = (el.value || '').trim();
        }

        if (value) map[key] = value;
      });
    return map;
  }

  function collectQuantity() {
    const qtyEl =
      document.getElementById('form-quantity-hidden') ||
      document.getElementById('Summary-Quantity') ||
      document.querySelector('.quantity__input') ||
      document.querySelector('input[name="quantity"]');
    return qtyEl && qtyEl.value ? String(qtyEl.value).trim() : '';
  }

  function collectTotalPrice() {
    const totalEl = document.querySelector(
      '.summary-actions [ymq-option-product-total-price] .ymq-price-span, .total-price-value, .custom-total-price'
    );
    return totalEl ? totalEl.textContent.trim() : '';
  }

  function collectYmqOptionInfoRaw() {
    const el = document.querySelector('input[name="properties[__ymq_option_info]"]');
    return el && el.value ? String(el.value) : '';
  }

  /** Human-readable option label -> value */
  function collectProductOptions() {
    const merged = {};
    const summary = collectFromSummaryList();
    const boxes = collectFromYmqBoxes();
    const properties = collectFromProperties();

    Object.keys(boxes).forEach(function (k) {
      merged[k] = boxes[k];
    });
    Object.keys(properties).forEach(function (k) {
      if (!merged[k]) merged[k] = properties[k];
    });
    Object.keys(summary).forEach(function (k) {
      merged[k] = summary[k];
    });

    return merged;
  }

  /** Flat map for Shopify contact[...] hidden fields */
  function buildContactFields(extra) {
    const fields = Object.assign({}, extra || {});
    const options = collectProductOptions();

    Object.keys(options).forEach(function (label) {
      const fieldName = contactFieldName(label);
      if (fieldName && options[label]) fields[fieldName] = options[label];
    });

    const qty = collectQuantity();
    if (qty) fields['contact[Quantity]'] = qty;

    const total = collectTotalPrice();
    if (total) fields['contact[Total Price]'] = total;

    const optionInfo = collectYmqOptionInfoRaw();
    if (optionInfo) fields['contact[YMQ Option Info]'] = optionInfo;

    const optionLabels = Object.keys(options);
    if (optionLabels.length) {
      fields['contact[Product Options]'] = optionLabels
        .map(function (label) {
          return label + ': ' + options[label];
        })
        .join(' | ');
    }

    return fields;
  }

  function removeInjectedFields(form) {
    if (!form) return;
    form.querySelectorAll('input[data-synced-quote-field="true"]').forEach(function (el) {
      el.remove();
    });
  }

  function injectContactFields(form, fields) {
    if (!form || !fields) return;
    removeInjectedFields(form);
    Object.keys(fields).forEach(function (name) {
      const value = fields[name];
      if (value === undefined || value === null || String(value) === '') return;

      let input = form.querySelector('[name="' + name.replace(/"/g, '\\"') + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-synced-quote-field', 'true');
        form.appendChild(input);
      }
      input.value = String(value);
      input.setAttribute('data-synced-quote-field', 'true');
    });
  }

  function collectForLocalStorage(productContext) {
    const fields = [];
    const options = collectProductOptions();
    Object.keys(options).forEach(function (label) {
      fields.push({ name: label, value: options[label] });
    });
    const qty = collectQuantity();
    if (qty) fields.push({ name: 'quantity', value: qty });
    const total = collectTotalPrice();
    if (total) fields.push({ name: 'Total Price', value: total });
    const optionInfo = collectYmqOptionInfoRaw();
    if (optionInfo) fields.push({ name: 'properties[__ymq_option_info]', value: optionInfo });

    return {
      is_used: false,
      timestamp: Date.now(),
      product: productContext || {},
      fields: fields,
    };
  }

  global.YearpinsQuoteCollect = {
    collectProductOptions: collectProductOptions,
    buildContactFields: buildContactFields,
    injectContactFields: injectContactFields,
    removeInjectedFields: removeInjectedFields,
    collectForLocalStorage: collectForLocalStorage,
    collectQuantity: collectQuantity,
    collectTotalPrice: collectTotalPrice,
  };
})(typeof window !== 'undefined' ? window : this);
